import { useAppSelector } from '.';
import { useCallback, useState } from 'react';

import { notifications } from '@mantine/notifications';

import useLogin from '@/hooks/useLogin.tsx';

import { selectAccessToken } from '@/store/user-process/user-selectors';

import { AppRoutes } from '@/router/routes';

import { useAuthorizeSsoMutation } from '@/api/authorization';

import {
	buildSSOSession,
	clearAuthProvider,
	createKeycloakClient,
	isKeycloakConfigured,
	setAuthProvider,
} from '@/shared/helpers/keycloak';

import { resetAuth } from '@/utils/resetAuth';

// Параметры callback-редиректа от Keycloak могут прийти как в query, так и в hash
export const hasSSOCallbackParams = () => {
	const rawSearch = window.location.search.startsWith('?')
		? window.location.search.slice(1)
		: window.location.search;
	const rawHash = window.location.hash.startsWith('#')
		? window.location.hash.slice(1)
		: window.location.hash;
	const params = new URLSearchParams(rawSearch || rawHash || '');

	return (
		params.has('code') && params.has('state') && (params.has('session_state') || params.has('iss'))
	);
};

export const isSSOLogoutCallback = () =>
	new URLSearchParams(window.location.search).get('sso_logged_out') === '1';

const useSSOAuth = () => {
	const [isLoading, setLoading] = useState(false);
	const accessToken = useAppSelector(selectAccessToken);
	const { completeLogin } = useLogin();
	const [authorizeSso] = useAuthorizeSsoMutation();

	const loginWithSSO = useCallback(async () => {
		try {
			setLoading(true);
			if (!isKeycloakConfigured()) {
				throw new Error('Не заполнена конфигурация Keycloak');
			}

			const keycloak = createKeycloakClient();
			await keycloak.init({ pkceMethod: 'S256', checkLoginIframe: false });
			await keycloak.login({ redirectUri: `${window.location.origin}${AppRoutes.Login}` });
		} catch (err) {
			console.error(err);
			notifications.show({ message: 'Ошибка запуска SSO авторизации', color: 'red' });
		} finally {
			setLoading(false);
		}
	}, []);

	const completeSSOLoginIfNeeded = useCallback(async () => {
		if (!hasSSOCallbackParams() || accessToken) {
			return;
		}

		try {
			setLoading(true);
			const keycloak = createKeycloakClient();
			const isAuthenticated = await keycloak.init({
				onLoad: 'check-sso',
				pkceMethod: 'S256',
				checkLoginIframe: false,
			});

			if (!isAuthenticated || !keycloak.token) {
				throw new Error('Keycloak токен не получен');
			}

			const user = await authorizeSso(keycloak.token).unwrap();
			const session = buildSSOSession(user, keycloak.token, keycloak.tokenParsed?.exp);

			window.history.replaceState({}, document.title, AppRoutes.Login);
			setAuthProvider('sso');
			await completeLogin(session);
		} catch (err) {
			console.error(err);
			notifications.show({ message: 'Ошибка SSO авторизации', color: 'red' });
		} finally {
			setLoading(false);
		}
	}, [accessToken, authorizeSso, completeLogin]);

	const completeSSOLogoutIfNeeded = useCallback(() => {
		if (!isSSOLogoutCallback()) {
			return;
		}

		window.history.replaceState({}, document.title, AppRoutes.Login);
		clearAuthProvider();
		resetAuth(false);
	}, []);

	return { loginWithSSO, completeSSOLoginIfNeeded, completeSSOLogoutIfNeeded, isLoading };
};

export default useSSOAuth;
